import React, { useCallback, useEffect, useState } from "react";
import {
  OrderListNavigationDirectionEnum,
  OrderTypeEnum,
  ReceiptTypeEnum,
} from "src/enums/order.enum";
import { CustomerOrder } from "src/interfaces/order.interface";
import CompletedOrderDetailsPanel from "src/features/Orders/components/CompletedOrderDetailsPanel";
import Spinner from "src/components/common/Spinner";
import OrderTypeFilterButtons from "src/components/common/OrderTypeFilterButtons";
import OrderDetailsDrawer, {
  PrintOptionsModal,
  PrintOptionsSelection,
} from "src/components/common/OrderDetailsDrawer";
import CompletedOrdersTable from "src/features/Orders/components/CompletedOrdersTable";
import { useCompletedOrders } from "src/features/Orders/hooks/useCompletedOrders";
import { useAuth } from "src/contexts/auth-provider";
import { useWebSocketSender } from "src/hooks/useSendMassageToWs";
import { failureToast, successToast } from "src/utils/toasts";

const PAGE_SIZE = 10;
const BORDER_LIGHT = "rgba(0,0,0,0.06)";

/** Build page list for pagination: e.g. [1, 2, 3, 4, 5, '...', 13] */
function getPaginationPages(currentPage: number, totalPages: number): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis", totalPages];
  }
  if (currentPage >= totalPages - 3) {
    return [1, "ellipsis", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [
    1,
    "ellipsis",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "ellipsis",
    totalPages,
  ];
}

interface CompletedOrdersPageProps {
  hideTitle?: boolean;
  /** When provided, header is hidden and search/filter use this state (unified control bar). */
  controlledSearchQuery?: string;
  onSearchChange?: (value: string) => void;
  controlledOrderTypeFilter?: OrderTypeEnum | null;
  onOrderTypeFilterChange?: (type: OrderTypeEnum | null) => void;
  dateRangeFrom?: string;
  dateRangeTo?: string;
}

const CompletedOrdersPage = ({
  hideTitle,
  controlledSearchQuery,
  onSearchChange,
  controlledOrderTypeFilter,
  onOrderTypeFilterChange,
  dateRangeFrom,
  dateRangeTo,
}: CompletedOrdersPageProps) => {
  const [printOptionsOpen, setPrintOptionsOpen] = useState(false);
  const { token } = useAuth();
  const { isSending, startConnection, stopConnection, sendMessage } = useWebSocketSender();
  const useControlBar =
    controlledSearchQuery !== undefined &&
    onSearchChange != null &&
    controlledOrderTypeFilter !== undefined &&
    onOrderTypeFilterChange != null;

  const {
    filteredCustomersOrder,
    selectedCustomerOrderIndex,
    pageInfo,
    currentPage,
    isCustomerOrdersLoading,
    showCompletedOrderDrawer,
    selectedCustomerOrder,
    handleUpdateOrder,
    orderTypeFilter,
    setOrderTypeFilter,
    manageOrderCardDetail,
    handleRowClick,
    setShowCompletedOrderDrawer,
    isUpdatingCustomerOrder,
    loadNextPage,
    loadPrevPage,
    goToFirstPage,
  } = useCompletedOrders(10, useControlBar
    ? {
        controlledSearchQuery,
        onSearchChange,
        controlledOrderTypeFilter,
        onOrderTypeFilterChange,
        from: dateRangeFrom,
        to: dateRangeTo,
      }
    : undefined);

  useEffect(() => {
    if (token) startConnection(token);
    return () => stopConnection();
  }, [token, startConnection, stopConnection]);

  const handlePrint = (order: CustomerOrder) => {
    handleRowClick(order, filteredCustomersOrder.findIndex((e) => e.node?.correlationId === order.correlationId) ?? 0);
  };

  const handlePrintFromDrawer = useCallback(() => {
    setPrintOptionsOpen(true);
  }, []);

  const handleConfirmPrintOptions = useCallback((options: PrintOptionsSelection) => {
    if (!selectedCustomerOrder) return;
    const receiptTypes: ReceiptTypeEnum[] = [];
    if (options.sendToKitchen) receiptTypes.push(ReceiptTypeEnum.KITCHEN);
    if (options.restaurantCopy) receiptTypes.push(ReceiptTypeEnum.RESTAURANT);
    if (options.customerCopy) receiptTypes.push(ReceiptTypeEnum.CUSTOMER);

    if (receiptTypes.length === 0) {
      failureToast("Please select at least one print option.");
      return;
    }

    let sentCount = 0;
    receiptTypes.forEach((receiptType) => {
      const sent = sendMessage("/app/manualPrinter", {
        customerOrderCorrelationId: selectedCustomerOrder.correlationId,
        receiptType,
      });
      if (sent) sentCount += 1;
    });

    if (sentCount === 0) {
      failureToast("Printer connection is not ready. Please try again.");
      return;
    }

    setPrintOptionsOpen(false);
    successToast("Print request sent.");
  }, [selectedCustomerOrder, sendMessage]);

  const totalPages = Math.max(1, pageInfo?.totalNumberOfPages ?? 1);
  const total = pageInfo?.totalNumberOfOrders ?? 0;
  const start = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const end = total === 0 ? 0 : Math.min(currentPage * PAGE_SIZE, total);
  const pageNumbers = getPaginationPages(currentPage, totalPages);

  const handlePageClick = (item: number) => {
    if (item === 1) goToFirstPage();
    else if (item === currentPage - 1) loadPrevPage();
    else if (item === currentPage + 1) loadNextPage();
  };

  const canGoToPage = (item: number) => {
    return item === 1 || item === currentPage - 1 || item === currentPage + 1;
  };

  return (
    <>
    <div className="w-full flex-1 min-h-0 flex flex-col min-w-0">
      {!useControlBar && (
        <div className="flex justify-between items-center flex-wrap gap-2 mb-4 shrink-0">
          {!hideTitle ? (
            <h1 className="text-lg font-semibold text-[#111827]">Completed Orders</h1>
          ) : (
            <span />
          )}
          <OrderTypeFilterButtons value={orderTypeFilter} onChange={setOrderTypeFilter} />
        </div>
      )}

      <div
        className="bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden flex-1 flex flex-col min-h-0 border border-[#F1F1F2]"
        style={{ borderColor: BORDER_LIGHT }}
      >
        {isCustomerOrdersLoading && !pageInfo ? (
          <div className="flex justify-center py-12 flex-1 min-h-[120px]">
            <Spinner />
          </div>
        ) : (filteredCustomersOrder ?? []).length === 0 ? (
          <p className="text-[#9B9B9B] py-12 text-center flex-1">No completed orders found.</p>
        ) : (
          <>
            <section
              className="flex-1 min-h-0 flex flex-col min-w-0 relative"
              aria-label="Completed orders table"
              aria-busy={isCustomerOrdersLoading}
            >
              {isCustomerOrdersLoading && (
                <div
                  className="absolute inset-0 z-20 flex items-center justify-center bg-white/70"
                  aria-hidden="true"
                >
                  <Spinner />
                </div>
              )}
              <div className="flex-1 min-h-0 min-w-0 flex flex-col">
                <CompletedOrdersTable
                  customerOrders={filteredCustomersOrder}
                  handleRowClick={handleRowClick}
                  onPrint={handlePrint}
                />
              </div>
            </section>

            {pageInfo && totalPages > 0 && (
              <section
                className="shrink-0 py-3.5 px-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t bg-[#fafafa] relative"
                style={{ borderColor: BORDER_LIGHT }}
                aria-label="Pagination"
              >
                <p className="text-sm text-gray-500 order-2 sm:order-1 sm:flex-1 sm:min-w-0 sm:text-left">
                  Showing {start}–{end} of {total}
                </p>
                <div className="flex items-center justify-center gap-2 flex-wrap order-1 sm:order-2 sm:flex-none sm:absolute sm:left-1/2 sm:-translate-x-1/2">
                  <button
                    type="button"
                    className="h-9 min-w-[56px] w-14 flex items-center justify-center text-sm font-medium text-[#252525] bg-white border border-[#F1F1F2] rounded-lg disabled:opacity-[0.5] disabled:cursor-default focus:outline-none hover:bg-[#fafafa]"
                    onClick={loadPrevPage}
                    disabled={isCustomerOrdersLoading || currentPage <= 1 || !pageInfo.hasPreviousPage}
                  >
                    Prev
                  </button>
                  <div className="flex items-center gap-1">
                    {pageNumbers.map((item, idx) =>
                      item === "ellipsis" ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="px-2 text-gray-400 text-sm"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          className={`min-w-[36px] w-9 h-9 rounded-lg text-sm font-medium flex items-center justify-center focus:outline-none ${
                            currentPage === item
                              ? "bg-orange text-white"
                              : "bg-gray-50 text-gray-700"
                          } disabled:opacity-[0.5] disabled:cursor-default`}
                          onClick={() => handlePageClick(item)}
                          disabled={isCustomerOrdersLoading || !canGoToPage(item) || currentPage === item}
                        >
                          {item}
                        </button>
                      )
                    )}
                  </div>
                  <button
                    type="button"
                    className="h-9 min-w-[56px] w-14 flex items-center justify-center text-sm font-medium text-[#252525] bg-white border border-[#F1F1F2] rounded-lg disabled:opacity-[0.5] disabled:cursor-default focus:outline-none hover:bg-[#fafafa]"
                    onClick={loadNextPage}
                    disabled={isCustomerOrdersLoading || currentPage >= totalPages || !pageInfo.hasNextPage}
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:block flex-1 min-w-0 order-3" aria-hidden="true" />
              </section>
            )}
          </>
        )}
      </div>
    </div>

    <OrderDetailsDrawer
      open={showCompletedOrderDrawer && !!selectedCustomerOrder}
      onClose={() => {
        setShowCompletedOrderDrawer(false);
        setPrintOptionsOpen(false);
      }}
      title="Order Details"
    >
      {selectedCustomerOrder && (
        <CompletedOrderDetailsPanel
          customerOrder={selectedCustomerOrder}
          onClose={() => setShowCompletedOrderDrawer(false)}
          handleUpdateOrder={handleUpdateOrder}
          isUpdatingCustomerOrder={isUpdatingCustomerOrder}
          onPrev={() => manageOrderCardDetail(OrderListNavigationDirectionEnum.DECREMENT)}
          onNext={() => manageOrderCardDetail(OrderListNavigationDirectionEnum.INCREMENT)}
          hasPrev={selectedCustomerOrderIndex != null && selectedCustomerOrderIndex > 0}
          hasNext={
            selectedCustomerOrderIndex != null &&
            filteredCustomersOrder.length > 0 &&
            selectedCustomerOrderIndex < filteredCustomersOrder.length - 1
          }
          onPrint={handlePrintFromDrawer}
        />
      )}
    </OrderDetailsDrawer>
    <PrintOptionsModal
      open={printOptionsOpen}
      onClose={() => setPrintOptionsOpen(false)}
      onConfirm={handleConfirmPrintOptions}
      isSending={isSending}
    />
    </>
  );
};

export default CompletedOrdersPage;
